import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import ProtectedRoute from './auth/ProtectedRoute'
import { AuthProvider } from './auth/AuthContext'
import PageVisibilityRoute from './auth/PageVisibilityRoute'
import Layout from './Layout'
import { UserRole } from './types/User'

const Login = lazy(() => import('./components/Auth/Login'))
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'))
const Agents = lazy(() => import('./components/Agents/Agents'))
const CustomTools = lazy(() => import('./components/CustomTools/CustomTools'))
const Knowledgebases = lazy(() => import('./components/Knowledgebases/Knowledgebases'))
const Contacts = lazy(() => import('./components/Contacts/Contacts'))
const Conversations = lazy(() => import('./components/Conversations/Conversations'))
const Profile = lazy(() => import('./components/Profile/Profile'))
const Calendar = lazy(() => import('./components/Calendar/Calendar'))
const Autocall = lazy(() => import('./components/Autocall/Autocall'))
const OutboundCalls = lazy(() => import('./components/OutboundCalls/OutboundCalls'))
const SpeechToText = lazy(() => import('./components/SpeechToText/SpeechToText'))
const Users = lazy(() => import('./components/Users/Users'))
const Voices = lazy(() => import('./components/Voices/Voices'))
const Todos = lazy(() => import('./components/Todos/Todos'))
const Tickets = lazy(() => import('./components/Tickets/Tickets'))
const DefaultAgentSettings = lazy(() => import('./components/DefaultAgentSettings/DefaultAgentSettings'))
const Tenants = lazy(() => import('./components/Tenants/Tenants'))
const Monitoring = lazy(() => import('./components/Monitoring/Monitoring'))
const Feedbacks = lazy(() => import('./components/Feedbacks/Feedbacks'))
const SystemPromptGenerator = lazy(() => import('./components/SystemPromptGenerator/SystemPromptGenerator'))
const OpeningHours = lazy(() => import('./components/OpeningHours/OpeningHours'))
const RedirectNumbers = lazy(() => import('./components/RedirectNumbers/RedirectNumbers'))
const Tokens = lazy(() => import('./components/Tokens/Tokens'))
const DialplanBot = lazy(() => import('./components/DialplanBot/DialplanBot'))
const DialplanOverview = lazy(() => import('./components/DialplanOverview/DialplanOverview'))
const TwilioNumbers = lazy(() => import('./components/TwilioNumbers/TwilioNumbers'))
const SystemMailLogs = lazy(() => import('./components/SystemMailLogs/SystemMailLogs'))

const appRoutes = [
  { id: 'dashboard', path: '/', element: <Dashboard />, index: true },
  { id: 'agents', path: '/agents', element: <Agents /> },
  { id: 'default-agent', path: '/systemprompt', element: <DefaultAgentSettings /> },
  { id: 'custom-tools', path: '/custom-tools', element: <CustomTools /> },
  { id: 'knowledge-base', path: '/knowledgebases', element: <Knowledgebases /> },
  { id: 'conversations', path: '/conversations', element: <Conversations /> },
  { id: 'contacts', path: '/contacts', element: <Contacts /> },
  { id: 'voices', path: '/voices', element: <Voices /> },
  { id: 'speech-to-text', path: '/speech-to-text', element: <SpeechToText /> },
  { id: 'outbound', path: '/outbound-calls', element: <OutboundCalls /> },
  { id: 'autocall', path: '/autocall', element: <Autocall /> },
  { id: 'todos', path: '/todos', element: <Todos /> },
  { id: 'tickets', path: '/tickets', element: <Tickets /> },
  { id: 'calendar', path: '/calendar', element: <Calendar /> },
  {
    id: 'systemprompt-generator',
    path: '/systemprompt-generator',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <SystemPromptGenerator />
      </ProtectedRoute>
    ),
  },
  { id: 'opening-hours', path: '/opening-hours', element: <OpeningHours /> },
  { id: 'redirect-numbers', path: '/redirect-numbers', element: <RedirectNumbers /> },
  { id: 'profile', path: '/profile', element: <Profile /> },
  {
    id: 'users',
    path: '/users',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
        <Users />
      </ProtectedRoute>
    ),
  },
  {
    id: 'tenants',
    path: '/tenants',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <Tenants />
      </ProtectedRoute>
    )
  },
  {
    id: 'feedbacks',
    path: '/feedbacks',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <Feedbacks />
      </ProtectedRoute>
    )
  },
  {
    id: 'monitoring',
    path: '/monitoring',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <Monitoring />
      </ProtectedRoute>
    )
  },
  {
    id: 'tokens',
    path: '/tokens',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <Tokens />
      </ProtectedRoute>
    )
  },
  {
    id: 'dialplan-bot',
    path: '/dialplan-bot',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
        <DialplanBot />
      </ProtectedRoute>
    )
  },
  {
    id: 'dialplan-overview',
    path: '/dialplan-overview',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
        <DialplanOverview />
      </ProtectedRoute>
    )
  },
  {
    id: 'twilio-numbers',
    path: '/twilio-numbers',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <TwilioNumbers />
      </ProtectedRoute>
    )
  },
  {
    id: 'system-mail-logs',
    path: '/system-mail-logs',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <SystemMailLogs />
      </ProtectedRoute>
    )
  }
];

const AppRoutes = () => {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6">
          <div className="mb-6 h-6 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`app-skel-${index}`}
                className="h-24 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"
              />
            ))}
          </div>
        </div>
      }
    >
      <Routes>
        {/* Public Routes */}
        <Route path='/login' element={<Login />} />
        {/* Alias for dashboard path to root (handles direct /dashboard access) */}
        <Route path='/dashboard' element={<Navigate to='/' replace />} />
        {/* Legacy alias for former default-agent page */}
        <Route path='/default-agent' element={<Navigate to='/systemprompt' replace />} />
        {/* Protected Routes */}
        <Route
          path='/'
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {appRoutes.map(route =>
            route.index ? (
              <Route
                key={route.id}
                index
                element={
                  <PageVisibilityRoute pageId={route.id}>
                    {route.element}
                  </PageVisibilityRoute>
                }
              />
            ) : (
              <Route
                key={route.id}
                path={route.path}
                element={
                  <PageVisibilityRoute pageId={route.id}>
                    {route.element}
                  </PageVisibilityRoute>
                }
              />
            )
          )}
        </Route>
        {/* Fallback to dashboard to avoid blank screens on unmatched routes */}
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App
